



import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import Tree from 'tui-tree';
import {
  AddTreeParamOptions,
  DeleteTreeParamOptions,
  TreeSelectEventProps,
  TreeBeforeMoveEventProps,
  CommonData,
  AccountData,
} from '@/types/banner';
import { indexInParent } from '@/utils/dataReplaceUtils';
import { isEqual } from 'underscore';

export interface TreeNodeData {
  displayOrder: number;
  text: string;
  groupNo?: number;
  sectionNo?: number;
}

@Component
export default class TuiTree extends Vue {
  @Prop({ required: true }) private readonly nodedata;
  @Prop({ required: true }) private readonly data;
  @Prop() private readonly onlySelectable!: boolean;
  @Prop() private readonly commonData!: CommonData;
  @Prop() private readonly accountsData!: AccountData[];
  @Prop() private readonly mallNo!: string;
  @Prop() private readonly onSaveData!: (isSectionAdditionAfterSaving?: boolean) => boolean
  private tree;
  private hasEditNode = false;
  private prevTreeOrder = 0;

  private getSelectNodeDepth(): number {
    const selectedNodeId = this.tree.getSelectedNodeId();
    return this.tree.getDepth(selectedNodeId);
  }

  @Watch('data')
  onDataChanged() {
    if (this.data.length === 0) return;

    this.topLevelIconReplacement();
  }

  @Watch('nodedata')
  onNodeDataChanged() {
    this.setSelectedNodeData('text', this.nodedata, false);
  }

  public addTree(
    nodeName = '',
    options: AddTreeParamOptions = {
      editAfterAdding: false,
      addToSelectedDepth: true,
    },
  ) {
    if (this.isDataChanged()) {
      return this.onSaveDataEmit();
    }

    const displayYn = 'N';
    let addedNodeId = 0;

    if (options.addToSelectedDepth) {
      const selectedNodeId = this.tree.getSelectedNodeId();
      const parentId = this.tree.getParentId(selectedNodeId);
      const depth = this.tree.getDepth(selectedNodeId);
      let hasChild = false;

      this.tree.open(selectedNodeId);

      this.tree.eachAll((node: any) => {
        if (node._parentId == selectedNodeId) {
          hasChild = true;

          return true;
        }
      });

      addedNodeId = this.tree.add(
        {
          depth: hasChild ? 2 : 1,
          text: nodeName,
          displayYn,
        },
        depth < 2 || hasChild ? selectedNodeId : parentId,
        { isSilent: depth == 5 },
      );
    } else {
      addedNodeId = this.tree.add({
        depth: 1,
        text: nodeName,
        displayYn,
      });

      const newNodeData = this.tree.getNodeData(addedNodeId);
      this.$emit('onAddTree', newNodeData);
    }

    this.addTreeAfterEvent(addedNodeId, options.editAfterAdding);
    this.topLevelIconReplacement();

    return;
  }

  private addTreeAfterEvent(addedNodeId: number, editAfterAdding = false) {
    this.tree.select(addedNodeId);

    if (editAfterAdding) {
      this.tree.editNode(addedNodeId);
      this.hasEditNode = true;
    }
  }

  public topLevelIconReplacement(): void {
    if (!this.$refs.tree) throw Error();

    this.$nextTick(() => {
      const iconEl = (this.$refs.tree as HTMLElement).querySelectorAll(
        '.tui-tree-root > .tui-tree-node > .tui-tree-content-wrapper .tui-ico-file',
      );
      iconEl.forEach(el => {
        el.classList.add('tui-ico-folder');
        el.classList.remove('tui-ico-file');
      });
    });
  }

  public removeSelectTree() {
    this.tree.remove(this.tree.getSelectedNodeId()[0]);
  }

  public deleteTree(
    options: DeleteTreeParamOptions = {
      deleteHasChildTree: false,
    },
  ): void {
    const selectedNodeId = this.tree.getSelectedNodeId();
    const depth = this.tree.getDepth(selectedNodeId);

    if (!selectedNodeId) {
      alert(this.$t('COMMON_TREE_ALERT_NOT_FOUND_ITEM'));
      return;
    }

    const hasChild = this.tree.getChildIds(selectedNodeId).length !== 0;

    if (!options.deleteHasChildTree && hasChild) {
      alert(this.$t('COMMON_TREE_ALERT_DELETE_GROUP'));
      return;
    }

    if (depth === 1) {
      if (confirm(this.$t('COMMON_TREE_CONFIRM_BEFORE_DELETE_SAVE_GROUP').toString())) this.$emit('onDeleteGroup');
    } else {
      if (confirm(this.$t('COMMON_TREE_CONFIRM_BEFORE_DELETE_SAVE_SECTION').toString())) {
        if (!this.commonData.sectionNo) {
          this.removeSelectTree();
          this.resetTree();
          this.tree.open(this.tree.getSelectedNodeId());
          return;
        }
        this.$emit('onDeleteSection');
      }
    }
  }

  private setSelectedNodeData(key: string, value: object | undefined, toAdd: boolean) {
    const selectedNodeId = this.tree.getSelectedNodeId();
    if (selectedNodeId) {
      const data = this.tree.getNodeData(selectedNodeId);
      if (data) {
        data[key] = toAdd ? data[key] + value : value;
        this.tree.setNodeData(selectedNodeId, data);
      }
    }
  }

  private mounted() {
    this.setTree();
  }

  private setTree() {
    this.tree = new Tree('#tree', {
      data: this.data,
      nodeDefaultState: 'opened',
      template: {
        internalNode: `
					<div class="tui-tree-content-wrapper">
            <button type="button" class="tui-tree-toggle-btn tui-js-tree-toggle-btn">
              <span class="tui-ico-tree"></span>
            </button>
            <span class="tui-tree-text tui-js-tree-text">
              <span class="tui-tree-ico tui-ico-folder"></span>
              {{text}}{{displayIcon}}
            </span>
          </div>
          <ul class="tui-tree-subtree tui-js-tree-subtree">{{children}}</ul>
				`,
        leafNode: `
					<div class="tui-tree-content-wrapper" style="padding-left: {{indent}}px">
            <span class="tui-tree-text tui-js-tree-text">
              <span class="tui-tree-ico tui-ico-file"></span>
              {{text}}{{displayIcon}}
            </span>
        	</div>
				`,
      },
    }).enableFeature('Selectable', {
      selectedClassName: 'tui-tree-selected',
    });
    if (!this.onlySelectable) {
      this.tree.enableFeature('Draggable', {
        helperClassName: 'tui-tree-drop',
        lineClassName: 'tui-tree-line',
        isSortable: true,
      });
    }

    this.tree.on('beforeSelect', (eventData: TreeSelectEventProps) => {
      if (this.isDataChanged()) {
        return this.onSaveDataEmit();
      }

      const prevNodeId = this.tree.getSelectedNodeId();
      const tobeNodeId = eventData.nodeId;
      const isSameSelect: boolean = prevNodeId === tobeNodeId;
      if (isSameSelect) return false;
      //if (Array.isArray(eventData.prevNodeId)) return false;

      return true;
    });

    this.tree.on('select', (eventData: TreeSelectEventProps) => {
      const nodeData = this.tree.getNodeData(eventData.nodeId);
      const depth = this.tree.getDepth(eventData.nodeId);
      const parentId = this.tree.getParentId(eventData.nodeId);
      const parentData = this.tree.getNodeData(parentId);

      this.nodePositionInsertURI(eventData.nodeId);

      this.tree.open(eventData.nodeId);

      this.$emit('changeSelect', {
        nodeData,
        depth,
        parentData,
        nodeId: eventData.nodeId,
      });
      this.topLevelIconReplacement();
    });

    this.tree.on('beforeMove', (eventData: TreeBeforeMoveEventProps) => {
      return this.onTreeBeforeMove(eventData);
    });

    this.tree.on('move', (eventData: TreeBeforeMoveEventProps) => {
      this.topLevelIconReplacement();

      const depth = this.tree.getDepth(eventData.nodeId);
      const el = document.getElementById(eventData.nodeId);
      const nextTreeOrder: number = indexInParent(el);
      const prevTree: TreeNodeData = this.tree.getNodeData(eventData.originalParentId);
      const nextTree: TreeNodeData = this.tree.getNodeData(eventData.nodeId);
      nextTree.displayOrder = nextTreeOrder;

      // tree node 자체를 return 해서 필요한 데이터를 사용할 수 있도록 전달
      if (depth === 1) this.$emit('onChangeOrder', { type: 'GROUP', prev: prevTree, next: nextTree });
      if (depth === 2) {
        const parentId = this.tree.getParentId(eventData.nodeId);
        const parentTree: TreeNodeData = this.tree.getNodeData(parentId);
        this.$emit('onChangeOrder', { type: 'SECTION', prev: prevTree, next: nextTree, parent: parentTree });
      }
    });
  }

  public resetTree(resetPosition = false): void {
    this.tree.resetAllData(this.data);

    if (resetPosition) {
      this.selectFirstNode();
      return;
    }

    const position = this.$route.query?.position;
    const hasPosition = !!position;

    if (!hasPosition) {
      this.selectFirstNode();
      return;
    }

    const positionArray: number[] = position
      .toString()
      .split('-')
      .map(v => Number(v));

    this.selectNodeFromPosition(positionArray);
  }

  private selectFirstNode(): void {
    const firstNode = this.tree.model.rootNode._childIds[0];
    this.tree.select(firstNode);
    this.tree.close(firstNode); // QA : 기본 선택 트리는 닫혀있어야 함.
  }

  private selectNodeFromPosition(positionArray: number[]): void {
    const targetNodeId = this.getNodeIdByPosition(positionArray);

    if (!targetNodeId) {
      this.selectFirstNode();
      return;
    }

    this.tree.select(targetNodeId);
    this.tree.open(targetNodeId, true);
  }

  private onTreeBeforeMove(eventData: TreeBeforeMoveEventProps): boolean {
    const parentDepth = this.tree.getDepth(eventData.newParentId);

    if (this.tree.getDepth(eventData.nodeId) >= 2) {
      // 3depth not allow
      if (parentDepth !== 1) {
        return false;
      }
    } else {
      if (parentDepth !== 0) {
        return false;
      }
    }
    if (this.isDataChanged()) {
      return this.onSaveDataEmit();
    }

    this.tree.open(eventData.newParentId);
    const el = document.getElementById(eventData.nodeId);
    this.prevTreeOrder = indexInParent(el);

    return true;
  }

  private isDataChanged(): boolean {
    const selectNodeDepth = this.getSelectNodeDepth();

    if (!this.tree.getSelectedNodeId()) return false;
    if (!selectNodeDepth) return false;

    if (selectNodeDepth === 1) {
      const groupDataIndex = this.tree.getNodeIndex(this.tree.getSelectedNodeId());
      const group = this.data[groupDataIndex];
      if (!group) return false;

      // isGroupTitleChanged
      return group.text !== this.commonData.title;
    } else {
      const beforeCommonData = JSON.parse(sessionStorage.getItem('beforeCommonData'));
      const beforeAccountsData = JSON.parse(sessionStorage.getItem('beforeAccountsData'));

      const hasCommonDataChanged = !isEqual(this.commonData, beforeCommonData);
      const hasAccountsDataChanged = !isEqual(this.accountsData, beforeAccountsData);

      // isSomeDataChanged
      return hasCommonDataChanged || hasAccountsDataChanged;
    }
  }

  private onSaveDataEmit(): boolean {
    if (this.getSelectNodeDepth() === 1) {
      if (confirm(this.$t('BANNER_LIST_ALERT_UNSAVED').toString())) {
        this.$emit('onSaveGroup');
        return true
      }
    } else {
      if (confirm(this.$t('BANNER_LIST_ALERT_UNSAVED').toString())) {
        return this.onSaveData(true)
      }
    }
    return false;
  }

  private getFamilyId(nodeId: string): string[] {
    const depth = this.tree.getDepth(nodeId);

    return Array(depth)
      .fill(nodeId)
      .map((nodeId, i, array) => {
        if (i === 0) return nodeId;

        const prevIndex = i - 1;
        return this.tree.getParentId(array[prevIndex]);
      });
  }

  private getNodesPosition(familyId: (string | string[])[]): number[] {
    return familyId.map(nodeId => {
      if (Array.isArray(nodeId)) {
        return this.tree.getNodeIndex(nodeId[0]);
      }
      return this.tree.getNodeIndex(nodeId);
    }).reverse();
  }

  private getNodeIdByPosition(positionArray: number[]): string {
    if (!positionArray.length) return;

    const treeInstanceModel = this.tree.model;
    return positionArray.reduce((nodeId, index) => {
      nodeId = !nodeId
        ? treeInstanceModel.rootNode._childIds[index]
        : treeInstanceModel.treeHash[nodeId]._childIds[index];

      return nodeId;
    }, '');
  }

  // Business logic
  private nodePositionInsertURI(nodeId: string): void {
    const familyId = this.getFamilyId(nodeId);
    const position = this.getNodesPosition(familyId).join('-');

    const currentQuery = JSON.parse(JSON.stringify(this.$route.query));

    if (currentQuery.position === position) return;

    const mergedQuery = Object.assign(currentQuery, { position });

    this.$router.push({
      query: mergedQuery,
    });
  }
}
